import React from "react"
import PropTypes from "prop-types"
import {Box, Grid, Typography, FormControlLabel} from "@mui/material"
import SelectComponent from "../select/select-component"
import GridRadioButton from "../radio-button/grid-radio-button"
import TextFieldInput from "../Input/text-field-input"
import MaskedTextFieldInput from "../Input/masked-text-field-input"
import InputAutoComplete from "../Input/Input-auto-complete"
import DynamicTextarea from "../Input/dynamic-textarea"
import StyledCheckbox from "../checkbox/styled-checkbox"

export const DEFAULT_RADIO_BOOLEAN = [
    {value: "sim", label: "Sim"},
    {value: "nao", label: "Não"},
]

const sanitizeDecimal = (val) => {
    if (val === undefined || val === null) return ""

    // Converte para string
    let str = `${val}`

    // Substitui pontos por vírgulas
    str = str.replace(/\./g, ",")

    // Remove tudo que não seja dígito ou vírgula
    str = str.replace(/[^0-9,]/g, "")

    // Substitui múltiplas vírgulas por apenas uma (mantendo a primeira)
    const parts = str.split(",")
    if (parts.length > 1) {
        str = parts[0] + "," + parts.slice(1).join("")
    }

    return str
}


const sanitizeInteger = (val) => {
    if (val === undefined || val === null) return ""
    return `${val}`.replace(/\D/g, "")
}

const normalizeUppercase = (val) => {
    if (val === undefined || val === null) return ""
    return `${val}`.toUpperCase()
}

const applyTransforms = (rawValue, field) => {
    let nextValue = rawValue

    if (field?.maskType === "integer" || field?.sanitize === "integer") {
        nextValue = sanitizeInteger(nextValue)
    }

    if (field?.maskType === "decimal") {
        nextValue = sanitizeDecimal(nextValue)
    }

    if (field?.normalize === "uppercase") {
        nextValue = normalizeUppercase(nextValue)
    }

    return nextValue
}


const getSpacing = (spacing) => {
    const defaultSpacing = {mt: 0.75, mb: 0, ml: 0.75, mr: 0, pt: 0, pb: 0, pl: 0, pr: 0}
    if (spacing === undefined || spacing === null) return defaultSpacing
    if (typeof spacing === "number") return {...defaultSpacing, mt: spacing, mb: spacing}
    return {
        mt: spacing.mt ?? defaultSpacing.mt,
        mb: spacing.mb ?? defaultSpacing.mb,
        ml: spacing.ml ?? defaultSpacing.ml,
        mr: spacing.mr ?? defaultSpacing.mr,
        pt: spacing.pt ?? defaultSpacing.pt,
        pb: spacing.pb ?? defaultSpacing.pb,
        pl: spacing.pl ?? defaultSpacing.pl,
        pr: spacing.pr ?? defaultSpacing.pr,
    }
}

export const FieldRenderer = React.memo(
    ({
         field,
         value,
         onChange,
         error,
         helperText,
         isFirstInRow = false,
         isLastInRow = false,
         sectionState = {},
         setField = () => {
         },
         autoCompleteController = {},
         showError = false
     }) => {

        // Validações de campo obrigatório
        const isEmpty =
            value === undefined ||
            value === null ||
            value === ""

        const isRequiredInvalid = field.required && showError && isEmpty

        const hasError = Boolean(error || isRequiredInvalid)

        const finalHelperText =
            helperText ||
            (isRequiredInvalid ? "Campo obrigatório" : undefined)

        const finalLabel = `${field.label}${field.required ? " *" : ""}`

        const handleChange = (raw) => onChange(applyTransforms(raw, field))
        const spacing = getSpacing(field.spacing)
        const keepSpacingOnFirst = Boolean(field.keepSpacingOnFirst)
        const keepSpacingOnLast = Boolean(field.keepSpacingOnLast)
        const dependsOnValue = field.dependsOn ? Boolean(sectionState?.[field.dependsOn]) : true
        const isDisabled = field.disabled || !dependsOnValue

        const responsiveMargin = (value, removeWhenFirstOrLast) =>
            removeWhenFirstOrLast ? {xxxs: value, md: 0} : value

    const wrapWithSpacing = (component, apply = true) =>
        apply ? (
            <Box
                sx={{
                    mt: spacing.mt,
                    mb: spacing.mb,
                    ml: keepSpacingOnFirst
                        ? spacing.ml
                        : responsiveMargin(spacing.ml, isFirstInRow),
                    mr: keepSpacingOnLast
                        ? spacing.mr
                        : responsiveMargin(spacing.mr, isLastInRow),
                    pt: spacing.pt,
                    pb: spacing.pb,
                    pl: spacing.pl,
                    pr: spacing.pr,
                    ...(field.sx || {}),
                }}
            >
                {component}
            </Box>
        ) : (
            component
        )

        const extraContent = (grid, content) => <Grid item xs={grid} md={grid} xl={grid}>{content}</Grid>

        const selectComponent = () => {
            return wrapWithSpacing(
                <Grid container spacing={2} direction="row" alignItems={"center"}>
                    <Grid item
                          xs={field.gridSpacing?.xs ?? 12}
                          md={field.gridSpacing?.md ?? 12}
                          xl={field.gridSpacing?.xl ?? 12}>
                        <SelectComponent
                            label={finalLabel}
                            value={value ?? ""}
                            onChange={(event) => handleChange(event.target.value)}
                            options={field.options}
                            width="100%"
                            error={hasError}
                            helperText={error ? helperText : undefined}
                        />
                    </Grid>
                    {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                </Grid>
            )
        }

        const dynamicTextAreaComponent = () => {
            const inputProps = {
                ...(field.inputProps || {}),
                ...(field.maxLength !== undefined ? {maxLength: field.maxLength} : {}),
            }
            const mergedInputProps = isDisabled && field.disabledOutlineOnFalse
                ? {
                    ...field.InputProps,
                    sx: {
                        ...(field.InputProps?.sx || {}),
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#a0a6b5 !important",
                        },
                    },
                }
                : field.InputProps
            return wrapWithSpacing(
                <Grid container spacing={2} direction="row" alignItems={"center"}>
                    <Grid item
                          xs={field.gridSpacing?.xs ?? 12}
                          md={field.gridSpacing?.md ?? 12}
                          xl={field.gridSpacing?.xl ?? 12}>
                        <DynamicTextarea
                            label={finalLabel}
                            value={value ?? ""}
                            onChange={(event) => handleChange(event.target.value)}
                            inputProps={inputProps}
                            InputProps={mergedInputProps}
                            minRows={field.minRows}
                            maxRows={field.maxRows}
                            blockEnterAfterLines={field.blockEnterAfterLines}
                            fullWidth
                            disabled={isDisabled}
                            error={hasError}
                            helperText={error ? helperText : undefined}
                        />
                    </Grid>
                    {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                </Grid>
            )
        }

        const radioComponent = () => {
            const radioSpacing = {
                ...spacing,
                ml: responsiveMargin(spacing.ml, isFirstInRow && !keepSpacingOnFirst),
                mr: responsiveMargin(spacing.mr, isLastInRow && !keepSpacingOnLast),
            }
            return (
                <Grid container spacing={2} direction="row" alignItems={"center"}>
                    <Grid item
                          xs={field.gridSpacing?.xs ?? 12}
                          md={field.gridSpacing?.md ?? 12}
                          xl={field.gridSpacing?.xl ?? 12}>
                        <GridRadioButton
                            title={finalLabel}
                            defaultRadioValue={field.defaultValue ?? ""}
                            value={value ?? field.defaultValue ?? ""}
                            onChange={(event) => handleChange(event.target.value)}
                            direction={field.direction || "row"}
                            columns={field.columns || (field.items?.length || 2)}
                            itemsPerColumn={field.itemsPerColumn || (field.items?.length || 2)}
                            radioButtonItems={field.items && field.items.length ? field.items : DEFAULT_RADIO_BOOLEAN}
                            spacing={radioSpacing}
                            horizontalSpacing={field.horizontalSpacing}
                        />
                    </Grid>
                    {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                </Grid>
            )
        }

        const checkboxComponent = () => {
            const handleCheckboxChange = (event) => {
                const checked = event.target.checked
                handleChange(checked)
                if (!checked && Array.isArray(field.clearOnFalse)) {
                    field.clearOnFalse.forEach((k) => setField(k, ""))
                }
            }
            return wrapWithSpacing(
                <FormControlLabel
                    control={
                        <StyledCheckbox
                            checked={Boolean(value)}
                            onChange={handleCheckboxChange}
                            inputProps={field.inputProps}
                            disabled={isDisabled}
                        />
                    }
                    label={
                        field.label ? (
                            <Typography variant="body2" color="common.white">
                                {field.label}
                            </Typography>
                        ) : null
                    }
                    sx={{mb: 0, ...(field.formControlSx || {})}}
                />
            )
        }

        const lineComponent = () => {
            return wrapWithSpacing(
                <Box
                    sx={{
                        height: "1px",
                        marginTop: 2,
                        marginBottom: 1,
                        width: "100%",
                        backgroundColor: "#FFF",
                    }}
                />,
                false
            )
        }

        const dividerComponent = () => {
            return wrapWithSpacing(
                <Typography
                    variant="contraHeading"
                    color="common.white"
                    sx={{
                        textTransform: "uppercase",
                        fontSize: ".65em",
                        color: "#F0F0F0",
                    }}
                >
                    {field.label}
                </Typography>,
                false
            )
        }

        const autoCompleteComponent = () => {
            console.log("Estou no tipo ativo")
            const source = field.autoCompleteSource || "default"
            const controller = autoCompleteController?.[source] || {}
            const controllerState = typeof controller.getState === "function"
                ? controller.getState(field.key)
                : controller.state || {}
            const handlers = controller.handlers || {}
            const meta = controller.meta || {}
            const options = meta.options || field.options || []
            const loading = Boolean(meta.loading)
            const displayKey = field.displayKey || `${field.key}Label`
            const invalidKey = field.invalidKey || `${field.key}NaoCadastrado`
            const controllerInvalid = controllerState.invalid
            const isLoadingOptions = Boolean(meta.loading) || Boolean(controllerState.pendingResolution)
            const invalid = controllerInvalid === undefined
                ? sectionState?.[invalidKey]
                : controllerInvalid

            const normalizeValue = React.useCallback(
                (val) => {
                    if (typeof meta.normalizeValue === "function") return meta.normalizeValue(val)
                    if (typeof val === "string") return val
                    return val?.value || ""
                },
                [meta]
            )

            React.useEffect(() => {
                if (typeof handlers.syncValue === "function") {
                    handlers.syncValue(field.key, value, sectionState?.[displayKey])
                }
            }, [displayKey, field.key, handlers, sectionState, value])

            React.useEffect(() => {
                if (!invalidKey) return
                const hasValue = Boolean(controllerState.value || controllerState.inputValue)
                // se ainda carregando opções, não marca como inválido
                const nextInvalid = isLoadingOptions ? false : (hasValue ? Boolean(controllerInvalid) : false)
                if (sectionState?.[invalidKey] !== nextInvalid) {
                    setField(invalidKey, nextInvalid)
                }
            }, [controllerInvalid, controllerState.inputValue, controllerState.value, isLoadingOptions, invalidKey, sectionState, setField])

            React.useEffect(() => {
                if (controllerState.value === undefined) return
                const nextValue = controllerState.value || ""
                if (sectionState?.[field.key] !== nextValue) {
                    setField(field.key, nextValue)
                }
            }, [controllerState.value, field.key, sectionState, setField])

            React.useEffect(() => {
                if (displayKey && controllerState.inputValue !== undefined) {
                    if (sectionState?.[displayKey] !== controllerState.inputValue) {
                        setField(displayKey, controllerState.inputValue)
                    }
                }
            }, [controllerState.inputValue, displayKey, sectionState, setField])

            const handleAutoChange = React.useCallback(
                (event, newValue, reason) => {
                    if (typeof handlers.onChange === "function") handlers.onChange(field.key, event, newValue, reason)
                    const normalized = normalizeValue(newValue)
                    handleChange(normalized)
                },
                [field.key, handleChange, handlers, normalizeValue]
            )

            const handleAutoInputChange = React.useCallback(
                (event, newInput) => {
                    if (typeof handlers.onInputChange === "function") handlers.onInputChange(field.key, event, newInput)
                    const normalized = normalizeValue(newInput)
                    handleChange(normalized)
                },
                [field.key, handleChange, handlers, normalizeValue]
            )

            const handleAutoBlur = React.useCallback(
                (event) => {
                    if (typeof handlers.onBlur === "function") handlers.onBlur(field.key, event)
                },
                [field.key, handlers]
            )

            const filterOptions =
                meta.filterOptions ||
                field.filterOptions ||
                undefined

            const minCharsToFilter =
                meta.minCharsToFilter ?? field.minCharsToFilter ?? 3

            const noOptionsText = meta.noOptionsText || field.noOptionsText
            const getOptionDisabled = meta.getOptionDisabled || field.getOptionDisabled

            return wrapWithSpacing(
                <Grid container spacing={2} direction="row" alignItems={"center"}>
                    <Grid item
                          xs={field.gridSpacing?.xs ?? 12}
                          md={field.gridSpacing?.md ?? 12}
                          xl={field.gridSpacing?.xl ?? 12}>
                        <InputAutoComplete
                            label={finalLabel}
                            value={controllerState.value ?? value ?? ""}
                            inputValue={controllerState.inputValue ?? ""}
                            options={options}
                            loading={loading}
                            freeSolo={meta.freeSolo ?? field.freeSolo ?? false}
                            getOptionLabel={meta.getOptionLabel}
                            onChange={handleAutoChange}
                            onInputChange={handleAutoInputChange}
                            onBlur={handleAutoBlur}
                            error={invalid || hasError}
                            helperText={(invalid ? field.helperText || "Não cadastrado" : finalHelperText) || helperText}
                            labelLeft={field.labelLeft ?? 0}
                            filterOptions={filterOptions}
                            minCharsToFilter={minCharsToFilter}
                            noOptionsText={noOptionsText}
                            filterKeys={meta.filterKeys || field.filterKeys}
                            normalizeFilter={meta.normalizeFilter || field.normalizeFilter}
                            getOptionDisabled={getOptionDisabled}
                        />
                    </Grid>
                    {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                </Grid>
            )
        }

        const textFieldComponent = () => {
            const displayValue = value ?? ""
            if (field.maskType || field.mask) {
                const mergedInputProps = isDisabled && field.disabledOutlineOnFalse
                    ? {
                        ...field.InputProps,
                        sx: {
                            ...(field.InputProps?.sx || {}),
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#a0a6b5 !important",
                            },
                        },
                    }
                    : field.InputProps

                return wrapWithSpacing(
                    <Grid container spacing={2} direction="row" alignItems={"center"}>
                        <Grid item
                              xs={field.gridSpacing?.xs ?? 12}
                              md={field.gridSpacing?.md ?? 12}
                              xl={field.gridSpacing?.xl ?? 12}>
                            <MaskedTextFieldInput
                                label={finalLabel}
                                value={displayValue}
                                onChange={(event) => handleChange(event.target.value)}
                                mask={field.mask}
                                maskType={field.maskType}
                                minDate={field.minDate}
                                maxDate={field.maxDate}
                                inputProps={field.inputProps}
                                fullWidth
                                disabled={isDisabled}
                                error={hasError}
                                helperText={finalHelperText}
                                InputProps={mergedInputProps}
                            />
                        </Grid>
                        {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                    </Grid>
                )
            }

            return wrapWithSpacing(
                <Grid container spacing={2} direction="row">
                    <Grid item
                          xs={field.gridSpacing?.xs ?? 12}
                          md={field.gridSpacing?.md ?? 12}
                          xl={field.gridSpacing?.xl ?? 12}>
                        <TextFieldInput
                            label={finalLabel}
                            value={value ?? ""}
                            onChange={(event) => handleChange(event.target.value)}
                            inputProps={{
                                ...(field.inputProps || {}),
                                ...(field.maxLength !== undefined ? {maxLength: field.maxLength} : {}),
                            }}
                            fullWidth
                            disabled={isDisabled}
                            error={hasError}
                            helperText={finalHelperText}
                            InputProps={
                                isDisabled && field.disabledOutlineOnFalse
                                    ? {
                                        ...field.InputProps,
                                        sx: {
                                            ...(field.InputProps?.sx || {}),
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#a0a6b5 !important",
                                            },
                                        },
                                    }
                                    : field.InputProps
                            }
                        />
                    </Grid>
                    {field.extraContent && extraContent(field.extraContent.fieldGrid, field.extraContent.content)}
                </Grid>
            )
        }

        switch (field.type) {
            case undefined:
                return textFieldComponent()
            case "select":
                return selectComponent()
            case "dynamicTextArea":
                return dynamicTextAreaComponent()
            case "radio":
                return radioComponent()
            case "checkbox":
                return checkboxComponent()
            case "divider":
                return dividerComponent()
            case "line":
                return lineComponent()
            case "autoComplete":
                return autoCompleteComponent()
            case "autocomplete":
                return autoCompleteComponent()
            default:
                return textFieldComponent()
        }
    })

FieldRenderer.displayName = "FieldRenderer"

FieldRenderer.propTypes = {
    field: PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        type: PropTypes.string,
        page: PropTypes.string,
        fieldComponent: PropTypes.element,
        options: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                }),
            ])
        ),
        items: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
            })
        ),
        direction: PropTypes.string,
        columns: PropTypes.number,
        itemsPerColumn: PropTypes.number,
        defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        md: PropTypes.number,
        mask: PropTypes.string,
        maskType: PropTypes.string,
        minDate: PropTypes.string,
        maxDate: PropTypes.string,
        inputProps: PropTypes.object,
        breakAfter: PropTypes.bool,
        sanitize: PropTypes.string,
        normalize: PropTypes.string,
        helperText: PropTypes.string,
        displayKey: PropTypes.string,
        invalidKey: PropTypes.string,
        longText: PropTypes.bool,
        maxLength: PropTypes.number,
        valueKey: PropTypes.string,
        checkedKey: PropTypes.string,
        blockEnterAfterLines: PropTypes.number,
        InputProps: PropTypes.object,
        dependsOn: PropTypes.string,
        disabled: PropTypes.bool,
        disabledOutlineOnFalse: PropTypes.bool,
        clearOnFalse: PropTypes.arrayOf(PropTypes.string),
        formControlSx: PropTypes.object,
        sx: PropTypes.object,
        spacing: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.shape({
                mt: PropTypes.number,
                mb: PropTypes.number,
                ml: PropTypes.number,
                mr: PropTypes.number,
                pt: PropTypes.number,
                pb: PropTypes.number,
                pl: PropTypes.number,
                pr: PropTypes.number,
            }),
        ]),
        horizontalSpacing: PropTypes.number,
        minRows: PropTypes.number,
        maxRows: PropTypes.number,
        keepSpacingOnFirst: PropTypes.bool,
        keepSpacingOnLast: PropTypes.bool,
        autoCompleteSource: PropTypes.string,
        freeSolo: PropTypes.bool,
        filterOptions: PropTypes.func,
        minCharsToFilter: PropTypes.number,
        labelLeft: PropTypes.number,
        noOptionsText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        filterKeys: PropTypes.arrayOf(PropTypes.string),
        normalizeFilter: PropTypes.func,
        getOptionDisabled: PropTypes.func,
        gridSpacing: {
            xs: PropTypes.number,
            md: PropTypes.number,
            xl: PropTypes.number,
        },
        extraContent: {
            fieldGrid: PropTypes.number,
            content: PropTypes.element
        }
    }).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    isFirstInRow: PropTypes.bool,
    isLastInRow: PropTypes.bool,
    sectionState: PropTypes.object,
    setField: PropTypes.func,
    autoCompleteController: PropTypes.object,
    ocr: PropTypes.bool,
    showError: PropTypes.bool,
}

export const renderGrid = (
    fields,
    sectionState = {},
    getFieldHandler = () => () => {
    },
    fieldErrors = {},
    gridProps = {},
    setField,
    autoCompleteController = {},
    showError = false
) => {
    let currentRowWidth = 0
    const setFieldHandler =
        typeof setField === "function"
            ? setField
            : (key, value) => {
                const handler = getFieldHandler(key)
                if (typeof handler === "function") handler(value)
            }

    return (
        <Grid container spacing={2} {...gridProps}>
            {fields.map((field) => {
                const md = field.md || 4
                const isFirstInRow = currentRowWidth === 0
                const projectedWidth = currentRowWidth + md
                const endsRow = projectedWidth >= 12 || field.breakAfter
                const isLastInRow = endsRow
                currentRowWidth = endsRow ? 0 : projectedWidth

                return (
                    <React.Fragment key={field.key}>
                        <Grid item xs={12} md={md}>
                            <FieldRenderer
                                field={field}
                                value={sectionState[field.key]}
                                onChange={getFieldHandler(field.key)}
                                error={fieldErrors[field.key]?.error}
                                helperText={fieldErrors[field.key]?.helperText}
                                isFirstInRow={isFirstInRow}
                                isLastInRow={isLastInRow}
                                sectionState={sectionState}
                                setField={setFieldHandler}
                                autoCompleteController={autoCompleteController}
                                showError={showError}
                            />
                        </Grid>
                        {field.breakAfter && <Grid item xs={12} sx={{p: 0, m: 0, paddingTop: '0px !important'}}/>}
                    </React.Fragment>
                )
            })}
        </Grid>
    )
}

export default FieldRenderer
